import "react-app-polyfill/ie9";
import "react-app-polyfill/stable";
import LogRocket from "logrocket";
import { Provider } from "react-redux";
import React from "react";
import ReactDOM from "react-dom";

// legacy styles
import "./styles/bootstrap/scss/bootstrap.scss";
import "./assets/sass/main.scss";
// new tailwind styles
import "./styles/main.css";
import "./styles/bootstrap/dist/js/bootstrap";
import "popper.js/dist/popper.min";

import App from "./containers/App";
import { ErrorBoundaries } from "./containers/ErrorBoundaries";
import { QueryProvider } from "./providers/QueryProvider";
import * as serviceWorker from "./serviceWorker";
import store from "./redux/store";
import { ToastProvider } from "./providers/ToastProvider";

const app = (
  <ErrorBoundaries>
    <QueryProvider>
      <ToastProvider>
        <Provider store={store}>
          <App />
        </Provider>
      </ToastProvider>
    </QueryProvider>
  </ErrorBoundaries>
);

ReactDOM.render(app, document.getElementById("root"));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

function sanitize(obj, allowedFields) {
  try {
    if (Array.isArray(obj)) {
      // Handle array type
      return obj.map((element) => sanitize(element, allowedFields));
    } else if (typeof obj === "object" && obj !== null) {
      // Handle object type
      let result = {};

      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          if (allowedFields.includes(key)) {
            // If it's an allowed field and its value is an object or array,
            // continue the sanitization process on that value.
            result[key] = sanitize(obj[key], allowedFields);
          } else {
            result[key] = "***";
          }
        }
      }

      return result;
    } else if (typeof obj === "string" && obj && obj.includes("{") && obj.includes("}")) {
      try {
        return sanitize(JSON.parse(obj), allowedFields);
      } catch (e) {
        return obj;
      }
    } else {
      // Handle base case
      return obj;
    }
  } catch (e) {
    return obj;
  }
}

const ALLOWED_HEADER_FIELDS = ["Accept", "content-type"];

const ALLOWED_BODY_FIELDS = [
  // commonly used top level field
  "data",

  // error response fields
  "code",
  "error",
  "request_id",

  // patient profile fields
  "account_updates",
  "assessment_completed_date",
  "assessment_config",
  "assessment_started_date",
  "assessment_status",
  "automatic_payment_enabled",
  "care_team_members",
  "carrier",
  "days_until_reset",
  "default_assessment",
  "eligibility_transaction_accepted",
  "email_subscriptions",
  "group_type",
  "has_active_card",
  "has_copay",
  "initial_prescriber_call",
  "initial_prescriber_call_date_time",
  "initial_video_consult",
  "initial_video_consult_date_time",
  "interested_in_guardianship",
  "is_sms_enabled",
  "marketing_communications",
  "ninety_day_care",
  "pcp_id",
  "pcp_practice_id",
  "preferences",
  "primary_insurance",
  "referral_id",
  "referral_source",
  "referred_date",
  "referrer_domain",
  "registration_date",
  "registration_device_type",
  "schedule_video_call",
  "schedule_video_call_date_time",
  "session_updates",
  "time_zone",
  "user",

  // common ids
  "entity_id",
  "patient_id",
  "patient_username",
  "provider_id",
  "provider_username",
  "user_id",
  "username",

  // audit fields
  "created_by",
  "created_date",
  "updated_by",
  "updated_date",

  // calendar event fields
  "allotted_time",
  "callId",
  "call_id",
  "date",
  "event_type",
  "provider_name",
  "provider_type",
  "time",
  "time_slots",
  "timestamp",
  "timezone",

  // common status/type fields
  "status",
  "type",

  // workflow engine
  "node_name",
  "workflow_config_entity_id",
  "workflow_instance_entity_id",
  "workflow_instance_sort_key",
  "workflow_name",
];

if (process.env.REACT_APP_ENABLE_LOG_ROCKET === "true") {
  LogRocket.init(process.env.REACT_APP_LOG_ROCKET_APP_ID, {
    dom: {
      inputSanitizer: true,
      textSanitizer: true,
    },
    network: {
      requestSanitizer: (request) => {
        request.headers = sanitize(request.headers, ALLOWED_HEADER_FIELDS);
        request.body = sanitize(request.body, ALLOWED_BODY_FIELDS);

        return request;
      },
      responseSanitizer: (response) => {
        response.headers = sanitize(response.headers, ALLOWED_HEADER_FIELDS);
        response.body = sanitize(response.body, ALLOWED_BODY_FIELDS);

        return response;
      },
    },
    rootHostname: "aptihealth.com",
  });
}
